// @react
import React, { useEffect, useRef, useState } from 'react'
// @typings
import { CreateAccountFormValues } from 'modules/common/forms/types/createAccount'
// @libraries
import {
  Control,
  Controller,
  UseFormMethods,
  FieldErrors,
} from 'react-hook-form'
// @components
import Grid from '@bit/atd.web.grid'
import InputCheckbox from '@bit/atd.web.forms.input-checkbox'
import TextLink from '@bit/atd.web.text-link'
// @module components
import { ExternalRouteNames } from 'constants/routeNames'
import { useQuery } from '@apollo/client'
import {
  GetConstantsQuery,
  GetConstantsQueryVariables,
} from 'lib/graphql/graphqlTypes'
import { GET_CONSTANTS } from 'graphql/queries/constants.query'

type PropsType = {
  formMethods: UseFormMethods<CreateAccountFormValues>
  control: Control<CreateAccountFormValues>
  errors: FieldErrors
}

const AcceptTermsFields = ({ formMethods, control, errors }: PropsType) => {
  /******************************************
   * REFS
   ******************************************/

  const termsRef = useRef<HTMLInputElement | null>(null)
  const GDPRRef1 = useRef<HTMLInputElement | null>(null)
  const GDPRRef2 = useRef<HTMLInputElement | null>(null)

  /****************************************
   *  GQL HOOKS
   ****************************************/
  const { data } = useQuery<GetConstantsQuery, GetConstantsQueryVariables>(
    GET_CONSTANTS,
    {
      fetchPolicy: 'cache-first',
    },
  )

  /****************************************
   *  HOOKS
   ****************************************/

  const [isGDPR, setisGDPR] = useState(false)
  const countryCode = formMethods.watch('countryCode')
  const gdprCountryCodes = data?.constants?.gdprCountryCodes
  useEffect(() => {
    if (gdprCountryCodes && countryCode) {
      setisGDPR(gdprCountryCodes.indexOf(countryCode) > -1)
    }
  }, [countryCode, gdprCountryCodes])

  /******************************************
   * RENDER
   ******************************************/

  return (
    <>
      <Grid xs={12} spacing={0} container>
        {/* Accept GDPR1 */}
        {isGDPR && (
          <Grid item>
            <Controller
              control={control}
              name="acceptGDPR1"
              onFocus={() => GDPRRef1?.current?.focus()}
              render={({ onBlur, value, name }) => (
                <>
                  <InputCheckbox
                    name={name}
                    label={
                      'I agree to receive communications by email and/or postal mail ' +
                      'from ATD about content and offerings that may be of interest to me.'
                    }
                    labelPlacement="right"
                    size="small"
                    checked={value as boolean}
                    inputRef={GDPRRef1}
                    onBlur={onBlur}
                    onChange={() =>
                      formMethods.setValue('acceptGDPR1', !value, {
                        shouldValidate: false,
                        shouldDirty: true,
                      })
                    }
                    error={!!errors?.acceptGDPR1}
                    errorText={errors?.acceptGDPR1?.message}
                  />
                </>
              )}
            />
          </Grid>
        )}

        {/* Accept GDPR2 */}
        {isGDPR && (
          <Grid item>
            <Controller
              control={control}
              name="acceptGDPR2"
              onFocus={() => GDPRRef2?.current?.focus()}
              render={({ onBlur, value, name }) => (
                <>
                  <InputCheckbox
                    name={name}
                    label={
                      'I agree to receive communications by email and/or postal mail from ATD’s partners, sponsors, ' +
                      'or suppliers about content and offerings that may be of interest to me'
                    }
                    labelPlacement="right"
                    size="small"
                    checked={value as boolean}
                    inputRef={GDPRRef2}
                    onBlur={onBlur}
                    onChange={() =>
                      formMethods.setValue('acceptGDPR2', !value, {
                        shouldValidate: false,
                        shouldDirty: true,
                      })
                    }
                    error={!!errors?.acceptGDPR2}
                    errorText={errors?.acceptGDPR2?.message}
                  />
                </>
              )}
            />
          </Grid>
        )}

        {/* Accept Terms */}
        <Grid item xs={12}>
          <Controller
            control={control}
            name="acceptTerms"
            onFocus={() => termsRef?.current?.focus()}
            render={({ onBlur, value, name }) => (
              <>
                <InputCheckbox
                  name={name}
                  label={
                    <>
                      Click here to accept and agree to ATD’s{' '}
                      <TextLink
                        href={ExternalRouteNames.ATD_TOS}
                        target="_blank"
                        inline
                      >
                        {' '}
                        Terms of Use
                      </TextLink>{' '}
                      and to indicate that you have reviewed the{' '}
                      <TextLink
                        href={ExternalRouteNames.ATD_TOS_PRIVACY}
                        target="_blank"
                        inline
                      >
                        Privacy Notice
                      </TextLink>
                      . By creating an account, you may receive communications
                      from ATD with content, offerings, or events that may be of
                      interest to you. Adjustments to your preferences can be
                      made at any time.
                    </>
                  }
                  labelPlacement="right"
                  size="small"
                  checked={value as boolean}
                  inputRef={termsRef}
                  onBlur={onBlur}
                  onChange={() =>
                    formMethods.setValue('acceptTerms', !value, {
                      shouldValidate: false,
                      shouldDirty: true,
                    })
                  }
                  error={!!errors?.acceptTerms}
                  errorText={errors?.acceptTerms?.message}
                />
              </>
            )}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default AcceptTermsFields
